<template>
  <div class="invoice-details-view">
    <div class="page-header">
      <div class="header-left">
        <button class="back-btn" @click="$router.push({ name: 'supplier-invoices' })">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div class="header-info">
          <h1 class="page-heading">Détails de la facture</h1>
          <p class="page-subtitle">Consultez et gérez la facture fournisseur</p>
        </div>
      </div>
      <div v-if="invoice && invoice.id" class="header-actions">
        <button class="action-btn secondary" @click="downloadPdf">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Télécharger
        </button>
        <button v-if="invoice.status !== 'paid' && invoice.status !== 'cancelled'" class="action-btn primary"
          @click="showPaymentModal = true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="12" y1="1" x2="12" y2="23"></line>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
          Ajouter un paiement
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Chargement des détails...</p>
    </div>

    <div v-else-if="!invoice || !invoice.id" class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <h3>Facture introuvable</h3>
      <p>Cette facture n'existe pas ou a été supprimée</p>
      <button class="back-to-list-btn" @click="$router.push({ name: 'supplier-invoices' })">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Retour à la liste
      </button>
    </div>

    <div v-else class="content-layout">
      <div class="main-content">
        <div class="card invoice-header-card">
          <div class="invoice-header">
            <div class="invoice-info">
              <div class="reference-section">
                <div class="reference-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                </div>
                <div class="reference-info">
                  <h2 class="reference-number">{{ invoice.invoice_number }}</h2>
                  <div class="date-info">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>{{ formatDate(invoice.invoice_date) }}</span>
                  </div>
                </div>
              </div>
              <div class="status-section">
                <span class="status-label">Statut</span>
                <span class="status-badge" :class="getStatusClass(invoice.status)">
                  <span class="status-dot"></span>
                  {{ getStatusLabel(invoice.status) }}
                </span>
              </div>
            </div>
          </div>

          <div class="invoice-details-grid">
            <div class="detail-card supplier-card">
              <div class="detail-header">
                <div class="detail-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <span class="detail-label">Fournisseur</span>
              </div>
              <div class="detail-content">
                <div class="supplier-avatar">
                  {{ getInitials(invoice.supplier?.name) }}
                </div>
                <div class="supplier-info">
                  <div class="supplier-name">{{ invoice.supplier?.name || '-' }}</div>
                  <div v-if="invoice.supplier?.phone || invoice.supplier?.email" class="supplier-meta">
                    <span v-if="invoice.supplier?.phone">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path
                          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z">
                        </path>
                      </svg>
                      {{ invoice.supplier.phone }}
                    </span>
                    <span v-if="invoice.supplier?.email">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      {{ invoice.supplier.email }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="detail-card purchase-order-card">
              <div class="detail-header">
                <div class="detail-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                </div>
                <span class="detail-label">Commande d'achat</span>
              </div>
              <div class="detail-content">
                <div v-if="invoice.purchase_order" class="po-info">
                  <div class="po-reference">{{ invoice.purchase_order.reference }}</div>
                  <button class="view-po-btn"
                    @click="$router.push({ name: 'purchase-order-details', params: { id: invoice.purchase_order.id } })">
                    Voir la commande
                  </button>
                </div>
                <div v-else class="no-po">-</div>
              </div>
            </div>

            <div v-if="invoice.notes" class="detail-card notes-card full-width">
              <div class="detail-header">
                <div class="detail-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <span class="detail-label">Remarques</span>
              </div>
              <div class="notes-content">
                {{ invoice.notes }}
              </div>
            </div>
          </div>
        </div>


        <div v-if="payments.length > 0" class="card payments-card">
          <div class="card-header">
            <div class="header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                <line x1="1" y1="10" x2="23" y2="10"></line>
              </svg>
            </div>
            <div class="header-text">
              <h3>Historique des paiements</h3>
              <p>{{ payments.length }} paiement{{ payments.length > 1 ? 's' : '' }} effectué{{
                payments.length > 1 ? 's' : '' }}</p>
            </div>
          </div>

          <div class="payments-list">
            <div v-for="payment in payments" :key="payment.id" class="payment-item">
              <div class="payment-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M9 11l3 3L22 4"></path>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                </svg>
              </div>
              <div class="payment-details">
                <div class="payment-method">{{ getPaymentMethodLabel(payment.payment_method) }}</div>
                <div class="payment-date">{{ formatDateTime(payment.payment_date) }}</div>
                <div v-if="payment.reference" class="payment-reference">Réf: {{ payment.reference }}</div>
              </div>
              <div class="payment-amount">{{ formatCurrency(payment.amount) }}</div>
              <button v-if="canDeletePayment" class="delete-payment-btn" @click="confirmDeletePayment(payment.id)">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="sidebar">
        <div class="card summary-card">
          <div class="summary-header">
            <div class="summary-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <h3>Résumé financier</h3>
          </div>

          <div class="summary-content">
            <div class="summary-item">
              <div class="summary-label">Montant total</div>
              <div class="summary-value">{{ formatCurrency(invoice.total_amount) }}</div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-item">
              <div class="summary-label">Montant payé</div>
              <div class="summary-value success">{{ formatCurrency(invoice.paid_amount) }}</div>
            </div>

            <div class="summary-divider"></div>

            <div class="summary-item highlight">
              <div class="summary-label">Solde restant</div>
              <div class="summary-value" :class="remainingAmount > 0 ? 'warning' : 'success'">
                {{ formatCurrency(remainingAmount) }}
              </div>
            </div>
          </div>

          <div v-if="remainingAmount > 0 && invoice.status !== 'cancelled'" class="quick-actions">
            <button class="quick-action-btn" @click="showPaymentModal = true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              Ajouter un paiement
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de paiement -->
    <transition name="modal">
      <div v-if="showPaymentModal" class="modal-overlay" @click="showPaymentModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <h3>Ajouter un paiement</h3>
            <button class="close-btn" @click="showPaymentModal = false">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>Montant <span class="required">*</span></label>
              <div class="input-with-icon">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                <input type="number" v-model="paymentForm.amount" placeholder="Montant du paiement"
                  :max="remainingAmount" />
              </div>
              <span class="field-hint">Solde restant: {{ formatCurrency(remainingAmount) }}</span>
            </div>

            <div class="form-group">
              <label>Méthode de paiement <span class="required">*</span></label>
              <select v-model="paymentForm.payment_method">
                <option value="">Sélectionner...</option>
                <option value="cash">Espèces</option>
                <option value="check">Chèque</option>
                <option value="bank_transfer">Virement bancaire</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="credit_card">Carte bancaire</option>
                <option value="other">Autre</option>
              </select>
            </div>

            <div class="form-group">
              <label>Date du paiement <span class="required">*</span></label>
              <div class="input-with-icon">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <input type="date" v-model="paymentForm.payment_date" />
              </div>
            </div>

            <div class="form-group">
              <label>Référence (optionnelle)</label>
              <input type="text" v-model="paymentForm.reference" placeholder="N° de chèque, référence virement..." />
            </div>

            <div class="form-group">
              <label>Notes (optionnelles)</label>
              <textarea v-model="paymentForm.notes" rows="3" placeholder="Notes additionnelles..."></textarea>
            </div>
          </div>
          <div class="modal-actions">
            <button class="modal-btn secondary" @click="showPaymentModal = false">
              Annuler
            </button>
            <button class="modal-btn primary" @click="submitPayment" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-small"></span>
              <template v-else>Enregistrer</template>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import supplierInvoiceService from '@/services/supplierInvoiceService'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'SupplierInvoiceDetailsView',
  setup() {
    const route = useRoute()
    const toastStore = useToastStore()

    const invoice = ref(null)
    const payments = ref([])
    const loading = ref(true)
    const isSubmitting = ref(false)
    const showPaymentModal = ref(false)

    const paymentForm = ref({
      amount: null,
      payment_method: '',
      payment_date: new Date().toISOString().split('T')[0],
      reference: '',
      notes: ''
    })

    const canDeletePayment = computed(() => {
      // Add logic to check if user can delete payments
      return true
    })

    const remainingAmount = computed(() => {
      const total = Number(invoice.value?.total_amount) || 0
      const paid = Number(invoice.value?.paid_amount) || 0
      return Math.max(0, total - paid)
    })

    const loadInvoice = async () => {
      loading.value = true
      try {
        const id = route.params.id
        const res = await supplierInvoiceService.getSupplierInvoice(id)
        invoice.value = res.data?.data || res.data || {}
        invoice.value.items = invoice.value.items || []

        // Load payments
        const paymentsRes = await supplierInvoiceService.getPayments(id)
        payments.value = paymentsRes.data?.data || paymentsRes.data || []
      } catch (e) {
        console.error('Error loading invoice:', e)
        invoice.value = null
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement de la facture',
          duration: 3000
        })
      } finally {
        loading.value = false
      }
    }

    const submitPayment = async () => {
      if (!paymentForm.value.amount || !paymentForm.value.payment_method) {
        toastStore.addToast({
          type: 'error',
          message: 'Veuillez remplir tous les champs obligatoires',
          duration: 3000
        })
        return
      }

      if (Number(paymentForm.value.amount) > remainingAmount.value) {
        toastStore.addToast({
          type: 'error',
          message: 'Le montant ne peut pas dépasser le solde restant',
          duration: 3000
        })
        return
      }

      try {
        isSubmitting.value = true
        await supplierInvoiceService.addPayment(invoice.value.id, paymentForm.value)
        toastStore.addToast({
          type: 'success',
          message: 'Paiement ajouté avec succès',
          duration: 3000
        })
        showPaymentModal.value = false
        paymentForm.value = {
          amount: null,
          payment_method: '',
          payment_date: new Date().toISOString().split('T')[0],
          reference: '',
          notes: ''
        }
        await loadInvoice()
      } catch (e) {
        console.error('Error adding payment:', e)
        toastStore.addToast({
          type: 'error',
          message: e.response?.data?.message || 'Erreur lors de l\'ajout du paiement',
          duration: 3000
        })
      } finally {
        isSubmitting.value = false
      }
    }

    const confirmDeletePayment = async (paymentId) => {
      if (!confirm('Êtes-vous sûr de vouloir supprimer ce paiement ?')) return

      try {
        await supplierInvoiceService.deletePayment(invoice.value.id, paymentId)
        toastStore.addToast({
          type: 'success',
          message: 'Paiement supprimé avec succès',
          duration: 3000
        })
        await loadInvoice()
      } catch (e) {
        console.error('Error deleting payment:', e)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de la suppression du paiement',
          duration: 3000
        })
      }
    }

    const downloadPdf = async () => {
      try {
        await supplierInvoiceService.downloadSupplierInvoicePdf(invoice.value.id)
        toastStore.addToast({
          type: 'success',
          message: 'Téléchargement en cours...',
          duration: 3000
        })
      } catch (e) {
        console.error('Error downloading PDF:', e)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du téléchargement du PDF',
          duration: 3000
        })
      }
    }

    const getStatusLabel = (status) => {
      return supplierInvoiceService.getStatusLabel(status)
    }

    const getStatusClass = (status) => {
      const map = {
        pending: 'status-pending',
        paid: 'status-paid',
        partial: 'status-partial',
        cancelled: 'status-cancelled',
        overdue: 'status-overdue'
      }
      return map[status] || 'status-pending'
    }

    const getPaymentMethodLabel = (method) => {
      return supplierInvoiceService.getPaymentMethodLabel(method)
    }

    const formatCurrency = (amount) => {
      return supplierInvoiceService.formatCurrency(amount)
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      return supplierInvoiceService.formatDate(dateString)
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    onMounted(loadInvoice)

    return {
      invoice,
      payments,
      loading,
      isSubmitting,
      showPaymentModal,
      paymentForm,
      canDeletePayment,
      remainingAmount,
      submitPayment,
      confirmDeletePayment,
      downloadPdf,
      getStatusLabel,
      getStatusClass,
      getPaymentMethodLabel,
      formatCurrency,
      formatDate,
      formatDateTime,
      getInitials
    }
  }
})
</script>

<style scoped>
/* Le style sera similaire à PurchaseOrderDetailsView, je l'ajoute ici */
.invoice-details-view {
  padding: 0 0 24px 0;
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #0A5C6B;
}

.back-btn svg {
  width: 20px;
  height: 20px;
  stroke: #475569;
  stroke-width: 2;
}

.header-info {
  flex: 1;
}

.page-heading {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
}

.action-btn.primary {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(10, 92, 107, 0.2);
}

.action-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.action-btn.secondary {
  background: white;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}

.action-btn.secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.action-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(10, 92, 107, 0.1);
  border-top-color: #0A5C6B;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-container p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 16px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon svg {
  width: 40px;
  height: 40px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.empty-state h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.back-to-list-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.back-to-list-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(10, 92, 107, 0.3);
}

.back-to-list-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.content-layout {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 24px;
}

.card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.card:last-child {
  margin-bottom: 0;
}

.invoice-header-card {
  padding: 24px;
}

.invoice-header {
  margin-bottom: 24px;
}

.invoice-info {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
}

.reference-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.reference-badge {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.reference-badge svg {
  width: 28px;
  height: 28px;
  stroke: #0284c7;
  stroke-width: 2;
}

.reference-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reference-number {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}

.date-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 14px;
}

.date-info svg {
  width: 16px;
  height: 16px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.status-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.status-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.status-badge.status-pending {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  color: #e65100;
}

.status-badge.status-pending .status-dot {
  background: #e65100;
  box-shadow: 0 0 0 3px rgba(230, 81, 0, 0.2);
}

.status-badge.status-partial {
  background: linear-gradient(135deg, #fff9c4 0%, #fff59d 100%);
  color: #f57f17;
}

.status-badge.status-partial .status-dot {
  background: #f57f17;
  box-shadow: 0 0 0 3px rgba(245, 127, 23, 0.2);
}

.status-badge.status-paid {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.status-badge.status-paid .status-dot {
  background: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
}

.status-badge.status-cancelled {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  color: #c62828;
}

.status-badge.status-cancelled .status-dot {
  background: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.2);
}

.status-badge.status-overdue {
  background: linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%);
  color: #c2185b;
}

.status-badge.status-overdue .status-dot {
  background: #c2185b;
  box-shadow: 0 0 0 3px rgba(194, 24, 91, 0.2);
}

.invoice-details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-card {
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
}

.detail-card.full-width {
  grid-column: 1 / -1;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.detail-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-icon svg {
  width: 18px;
  height: 18px;
  stroke: #0A5C6B;
  stroke-width: 2;
}

.detail-label {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
}

.detail-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.supplier-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.supplier-info {
  flex: 1;
}

.supplier-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.supplier-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
}

.supplier-meta span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.supplier-meta svg {
  width: 14px;
  height: 14px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.po-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.po-reference {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.view-po-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: white;
  color: #0A5C6B;
  border: 1.5px solid #0A5C6B;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.view-po-btn:hover {
  background: #0A5C6B;
  color: white;
}

.no-po {
  color: #94a3b8;
  font-style: italic;
}

.notes-content {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 2px solid #f1f5f9;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
}

.header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon svg {
  width: 20px;
  height: 20px;
  stroke: #0284c7;
  stroke-width: 2;
}

.header-text h3 {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.header-text p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.items-table-wrapper {
  overflow-x: auto;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
}

.items-table thead {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
}

.items-table th {
  padding: 16px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.th-content svg {
  width: 16px;
  height: 16px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.items-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
}

.item-row:last-child td {
  border-bottom: none;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-icon svg {
  width: 18px;
  height: 18px;
  stroke: #0284c7;
  stroke-width: 2;
}

.product-name {
  font-weight: 600;
  color: #0f172a;
}

.product-ref {
  font-size: 12px;
  color: #64748b;
}

.quantity-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  color: #0284c7;
  border-radius: 8px;
  font-weight: 700;
  font-size: 14px;
}

.price-value,
.total-value {
  font-weight: 600;
  color: #0f172a;
}

.total-value {
  font-size: 15px;
  font-weight: 700;
}

.payments-list {
  padding: 16px 24px;
}

.payment-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 12px;
  transition: all 0.2s;
}

.payment-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.payment-item:last-child {
  margin-bottom: 0;
}

.payment-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.payment-icon svg {
  width: 20px;
  height: 20px;
  stroke: #2e7d32;
  stroke-width: 2;
}

.payment-details {
  flex: 1;
}

.payment-method {
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.payment-date {
  font-size: 13px;
  color: #64748b;
}

.payment-reference {
  font-size: 12px;
  color: #94a3b8;
  font-style: italic;
  margin-top: 2px;
}

.payment-amount {
  font-size: 18px;
  font-weight: 800;
  color: #2e7d32;
}

.delete-payment-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.delete-payment-btn:hover {
  background: #c62828;
  border-color: #c62828;
}

.delete-payment-btn svg {
  width: 18px;
  height: 18px;
  stroke: #64748b;
  stroke-width: 2;
  transition: stroke 0.2s;
}

.delete-payment-btn:hover svg {
  stroke: white;
}

.summary-card {
  position: sticky;
  top: 24px;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-bottom: 2px solid #f1f5f9;
}

.summary-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-icon svg {
  width: 20px;
  height: 20px;
  stroke: #2e7d32;
  stroke-width: 2;
}

.summary-header h3 {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.summary-content {
  padding: 20px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-item.highlight {
  padding: 16px;
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  margin-left: -4px;
  margin-right: -4px;
}

.summary-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.summary-value {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.summary-value.success {
  color: #2e7d32;
}

.summary-value.warning {
  color: #e65100;
}

.summary-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 16px 0;
}

.quick-actions {
  padding: 16px 20px;
  border-top: 2px solid #f1f5f9;
}

.quick-action-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.quick-action-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px;
  border-bottom: 2px solid #f1f5f9;
  position: relative;
}

.modal-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.modal-icon svg {
  width: 24px;
  height: 24px;
  stroke: #0284c7;
  stroke-width: 2;
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  flex: 1;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #e2e8f0;
}

.close-btn svg {
  width: 18px;
  height: 18px;
  stroke: #64748b;
  stroke-width: 2;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 8px;
}

.required {
  color: #c62828;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  transition: all 0.2s;
  outline: none;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.input-with-icon {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  stroke: #94a3b8;
  stroke-width: 2;
  pointer-events: none;
}

.input-with-icon input {
  padding-left: 44px;
}

.field-hint {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-top: 6px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 2px solid #f1f5f9;
  background: #fafbfc;
}

.modal-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-btn.primary {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  color: white;
}

.modal-btn.primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.modal-btn.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-btn.secondary {
  background: white;
  color: #475569;
  border: 1.5px solid #e2e8f0;
}

.modal-btn.secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  display: inline-block;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.9);
}

@media (max-width: 1024px) {
  .content-layout {
    grid-template-columns: 1fr;
  }

  .summary-card {
    position: static;
  }
}

@media (max-width: 768px) {
  .invoice-details-view {
    padding: 0 0 16px 0;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    flex-direction: column;
    align-items: stretch;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .invoice-details-grid {
    grid-template-columns: 1fr;
  }

  .items-table-wrapper {
    margin: 0 -24px;
  }

  .items-table {
    min-width: 600px;
  }
}
</style>
